/**
 * ConfirmPillarPanel — the "these are my picks" gate at the end of
 * each pillar's tool. Renders a small confirmation card with the
 * currently-selected items, a cap indicator, and a "Confirm" CTA
 * that writes the choices to the journey profile.
 *
 * Props:
 *   pillar:    "pick" | "place" | "prove" | "apply"
 *   choices:   array of already-selected item objects (opaque to us)
 *   min:       minimum items required to allow confirmation
 *   cap:       maximum items allowed
 *   label:     display label for a single item (e.g. "subject", "city")
 *   labelPlural: display label for multiple (e.g. "subjects")
 *   renderItem: (item) => string  → how each item is rendered in the summary
 *   nextTo:    optional route to navigate to after confirm
 *   nextLabel: optional label for the "next" nav link
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useJourney } from "@/context/JourneyContext";

// Where each pillar's confirmed choices live in the journey (mirrors the backend's confirm targets).
const SAVED_AT = { pick: ["what", "subjects_confirmed"], place: ["where", "cities_confirmed"], prove: ["prove", "chances_confirmed"], apply: ["how", "shortlist_confirmed"] };

const PILLAR_COPY = {
  pick:  { verb: "subject",       verbPlural: "subjects",      colour: "#7C3AED", next: "Next: match a city" },
  place: { verb: "city",          verbPlural: "cities",        colour: "#0891B2", next: "Next: shortlist universities" },
  prove: { verb: "university",    verbPlural: "universities",  colour: "#002147", next: "Next: pick who to apply to" },
  apply: { verb: "application",   verbPlural: "applications",  colour: "#92400E", next: "You're ready to apply" },
};

export default function ConfirmPillarPanel({
  pillar,
  choices = [],
  min = 1,
  cap = 3,
  renderItem = (i) => (typeof i === "string" ? i : (i?.label || i?.name || i?.slug || "")),
  nextTo,
  nextLabel,
  onConfirmed,
  confirmLabel,        // optional override for the CTA text (unconfirmed state)
  itemNoun,            // optional override for what to call each row in the "min needed" copy
  testId = "confirm-panel",
  blockedReason = null,  // optional: disables confirm until the caller's review is done
  stayOnPage = false,    // save in place; "next" becomes a separate link instead of an automatic jump
  savedChoices = null,   // labels currently confirmed; equal to the choices = "Saved" state
}) {
  const { confirmPillar, pillarProgress, profile: journey } = useJourney();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const already = pillarProgress?.[pillar]?.completed;
  const copy = PILLAR_COPY[pillar] || {};
  const displayNext = nextLabel || copy.next;
  const nItems = choices.length;
  const enough = nItems >= min;
  const over   = nItems > cap;
  const shortBy = Math.max(0, min - nItems);
  const noun    = itemNoun || copy.verb || "item";
  const nounPl  = itemNoun ? `${itemNoun}s` : (copy.verbPlural || `${copy.verb || "item"}s`);
  const labels = choices.map(renderItem);
  const [parent, field] = SAVED_AT[pillar] || [];
  const saved = savedChoices || (parent ? ((journey?.[parent] || {})[field] || []).map(renderItem) : null);
  const inSync = stayOnPage && !!saved?.length && saved.length === labels.length && labels.every((l) => saved.includes(l));

  const handle = async () => {
    if (!enough || over || busy || blockedReason) return;
    setBusy(true);
    const res = await confirmPillar(pillar, choices);
    setBusy(false);
    if (res?.ok) {
      const word = nItems === 1 ? copy.verb : (copy.verbPlural || `${copy.verb}s`);
      toast.success(`${word ? word[0].toUpperCase() + word.slice(1) : "Choice"} ${stayOnPage ? "saved" : "confirmed"}.`, {
        description: `Saved to your journey. ${nItems} ${nItems === 1 ? copy.verb : (copy.verbPlural || copy.verb + "s")} locked in.`,
      });
      onConfirmed?.(res.profile);
      if (nextTo && !stayOnPage) setTimeout(() => nav(nextTo), 400);
    } else {
      toast.error("Couldn't save your choice. Please try again.");
    }
  };

  return (
    <div
      data-testid={testId}
      className="rounded-xl border-2 border-dashed p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
      style={{ borderColor: copy.colour + "66", background: copy.colour + "08" }}
    >
      <div
        className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full shrink-0"
        style={{ background: copy.colour + "1F", color: copy.colour }}
      >
        <Sparkles className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: copy.colour }}>
          {inSync ? `Your ${cap > 1 ? nounPl : noun} · saved` : <>Confirm your choice{cap > 1 && "s"}</>}
        </div>
        {inSync && (
          <p className="text-[12px] text-stone-600 mt-0.5" data-testid={`${testId}-in-sync`}>
            {pillar === "pick"
              ? "Cities, courses and chances use these. To change them, use Choose or Swap in above, then save."
              : "Saved to your journey. Change your selection above, then save again."}
          </p>
        )}
        {nItems === 0 ? (
          <p className="text-sm text-stone-700 mt-0.5">
            Pick up to <span className="font-semibold">{cap}</span> {cap === 1 ? noun : nounPl} to save to your journey and unlock the next stage.
          </p>
        ) : (
          <div className="mt-1" data-testid={`${testId}-choices`}>
            <div className="text-[13px] text-stone-800 font-semibold mb-1">
              {nItems}/{cap} chosen
            </div>
            <div className="flex flex-wrap gap-1.5">
              {choices.slice(0, cap).map((c, i) => {
                const label = renderItem(c);
                if (!label) return null;
                return (
                  <span
                    key={c?.slug || c?.id || `choice-${i}`}
                    data-testid={`${testId}-choice-${i}`}
                    className="inline-flex items-center rounded-full bg-white/70 border border-stone-200 px-2 py-0.5 text-[11.5px] text-stone-700"
                    title={label}
                  >
                    <span className="font-mono text-stone-400 mr-1">{i + 1}.</span>
                    <span className="max-w-[220px] truncate">{label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {over && (
          <p className="text-[11px] text-rose-700 mt-1" data-testid={`${testId}-over-cap`}>
            You've selected {nItems} — max is {cap}. Please remove {nItems - cap} to continue.
          </p>
        )}
        {blockedReason && (
          <p className="text-[12px] text-amber-800 mt-1" data-testid={`${testId}-blocked`}>{blockedReason}</p>
        )}
        {!enough && (
          <div
            data-testid={`${testId}-min-warning`}
            className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 flex items-start gap-2"
          >
            <span aria-hidden="true" className="text-amber-700 leading-none mt-0.5">⚠</span>
            <p className="text-[12px] text-amber-900 leading-snug">
              You need at least <strong>{min}</strong> {min === 1 ? noun : nounPl} to confirm — add{" "}
              <strong>{shortBy}</strong> more to unlock this stage.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <Button
          type="button"
          onClick={handle}
          disabled={!enough || over || busy || !!blockedReason || inSync}
          data-testid={`${testId}-confirm`}
          className="rounded-md text-white"
          style={enough && !over && !busy && !blockedReason ? { backgroundColor: copy.colour } : {}}
        >
          {busy
            ? "Saving…"
            : inSync
              ? <>Saved<Check className="h-4 w-4 ml-1.5" /></>
            : stayOnPage && already
              ? <>Save {nItems === 1 ? noun : nounPl}<Check className="h-4 w-4 ml-1.5" /></>
            : already
              ? <>Update {nItems === 1 ? noun : nounPl}<Check className="h-4 w-4 ml-1.5" /></>
              : confirmLabel
                ? <>{confirmLabel}<Check className="h-4 w-4 ml-1.5" /></>
                : <>Confirm {nItems > 0 ? `${nItems} ${nItems === 1 ? noun : nounPl}` : "my choice"}<Check className="h-4 w-4 ml-1.5" /></>}
        </Button>
        {displayNext && (stayOnPage && nextTo ? (
          <Link to={nextTo} data-testid={`${testId}-next`}
            className="inline-flex min-h-[40px] items-center rounded-md border border-stone-300 bg-white px-3 text-sm text-stone-800 transition-colors hover:border-stone-500">
            {displayNext} →
          </Link>
        ) : (
          <span className="hidden lg:inline text-[11px] text-stone-500 italic max-w-[160px]">
            {displayNext} →
          </span>
        ))}
      </div>
    </div>
  );
}
